/**
 * The one place a caller turns "I need a credential" into a usable token.
 *
 * Both transports go through this. The WebSocket path grew a good version of this
 * logic first; the HTTP path had none at all and simply sent whatever was in
 * storage, which is what let an expired token reach the server and come back as a
 * misleading policy denial.
 *
 * THE CONTRACT, which callers depend on:
 *
 *   returns null  -> there is no session. Send the request anonymously; public
 *                    reads must keep working for logged-out visitors.
 *   returns token -> use exactly this token.
 *   throws        -> there IS a session and it is unusable. NEVER fall back to an
 *                    anonymous request, or the caller lands in policy as an
 *                    anonymous user and gets the same misleading 403 this whole
 *                    change exists to remove.
 *
 * Four corrections over the WebSocket original, each of which was a real defect:
 *
 *   - No sticky module-global failure. The original latched any refresh failure
 *     into `ambientAuthFailure` and rethrew it on every later call, so ONE
 *     transient network blip disabled refresh until an unrelated auth-state
 *     change reset it.
 *   - "Expiring soon" and "already expired" are different. The original collapsed
 *     both behind a 60s buffer, so a transient refresh failure threw away a token
 *     that was still valid for another 59 seconds.
 *   - A missing or non-numeric `exp` is unusable, not fresh. `payload.exp * 1000`
 *     is NaN, and `now > NaN` is false, so such a token read as perfectly fresh.
 *   - Storage is cleared only on a DEFINITIVE rejection, never on a transient one.
 */
import { getConfig } from '../client/config';
import { decodeBase64Url } from './core-platform';
import { refreshSession } from './auth-api';
import { isDefinitiveRejection } from './refresh-error';
import { getActiveSessionManager } from './session-manager';
import { ServerSessionManager } from './server-session-manager';
/**
 * Stable machine code for "there is a session and it cannot be used".
 *
 * The VALUE is deliberately unchanged from when this lived in subscription-v2 as
 * `WS_AUTH_EXPIRED_CODE`: it is public surface that consumers branch on
 * (`e.code === 'auth_expired'`), so renaming it would break them for no gain. Only
 * the identifier lost its `WS_` prefix, now that HTTP raises it too.
 *
 * The realtime worker's 401 body uses `invalid_or_expired_session` for the same
 * condition on the wire; this is the client-side counterpart.
 */
export const AUTH_EXPIRED_CODE = 'auth_expired';
/** The principal changed under a request that was already in flight. */
export const AUTH_CHANGED_CODE = 'auth_changed';
export function makeAuthExpiredError(message, status) {
    const err = new Error(`${AUTH_EXPIRED_CODE}: ${message}`);
    err.code = AUTH_EXPIRED_CODE;
    if (status !== undefined)
        err.status = status;
    return err;
}
export function makeAuthChangedError(message) {
    const err = new Error(`${AUTH_CHANGED_CODE}: ${message}`);
    err.code = AUTH_CHANGED_CODE;
    return err;
}
export function isAuthExpiredError(error) {
    const code = error === null || error === void 0 ? void 0 : error.code;
    return !!error && typeof error === 'object'
        && (code === AUTH_EXPIRED_CODE || code === AUTH_CHANGED_CODE);
}
export function normalizeAuthExpiredError(error, fallbackMessage) {
    var _a, _b;
    if (isAuthExpiredError(error))
        return error;
    const status = (_b = (_a = error === null || error === void 0 ? void 0 : error.response) === null || _a === void 0 ? void 0 : _a.status) !== null && _b !== void 0 ? _b : error === null || error === void 0 ? void 0 : error.code;
    return makeAuthExpiredError(fallbackMessage, status);
}
/** Refresh this many ms before `exp`, so a token cannot die mid-flight. */
export const TOKEN_EXPIRY_SKEW_MS = 60000;
export function classifyToken(token, nowMs = Date.now()) {
    let exp;
    try {
        const payload = JSON.parse(decodeBase64Url(token.split('.')[1]));
        exp = payload === null || payload === void 0 ? void 0 : payload.exp;
    }
    catch (_a) {
        return 'unusable';
    }
    // A token with no numeric exp used to compare against NaN and read as fresh.
    if (typeof exp !== 'number' || !Number.isFinite(exp))
        return 'unusable';
    const expiresAtMs = exp * 1000;
    if (nowMs >= expiresAtMs)
        return 'expired';
    if (nowMs >= expiresAtMs - TOKEN_EXPIRY_SKEW_MS)
        return 'expiring-soon';
    return 'fresh';
}
/** Short, non-secret fingerprint used only to key the in-flight map. Never logged. */
export function tokenFingerprint(token) {
    let h = 5381;
    for (let i = 0; i < token.length; i++)
        h = ((h << 5) + h + token.charCodeAt(i)) >>> 0;
    return h.toString(16);
}
/**
 * In-flight refresh TRANSACTIONS, keyed by the session they act on.
 *
 * The issuer-level dedupe in `refreshSession` shares only the network response;
 * each caller then commits independently. With a generation check that means the
 * first commit wins and every other caller sees storage it does not recognise —
 * so a burst of ordinary requests at expiry would all fail as "the user changed".
 * Deduping the WHOLE snapshot -> refresh -> commit makes them share one outcome.
 *
 * Keyed by generation as well as app, so a caller that arrives after a re-login
 * starts its own transaction instead of joining a stale one.
 */
const refreshTransactions = new Map();
function transactionKey(scope, appId, generation, refreshToken) {
    return `${scope}:${appId}:${generation !== null && generation !== void 0 ? generation : 'none'}:${tokenFingerprint(refreshToken)}`;
}
async function readSessionParts(isServer) {
    var _a, _b, _c, _d, _e, _f;
    if (isServer) {
        const session = await ServerSessionManager.instance.getSession();
        return {
            idToken: (_a = session === null || session === void 0 ? void 0 : session.idToken) !== null && _a !== void 0 ? _a : null,
            refreshToken: (_b = session === null || session === void 0 ? void 0 : session.refreshToken) !== null && _b !== void 0 ? _b : null,
            issuer: undefined,
            generation: null,
        };
    }
    const mgr = getActiveSessionManager();
    const session = await mgr.readSessionWithGeneration();
    return {
        idToken: (_c = session === null || session === void 0 ? void 0 : session.idToken) !== null && _c !== void 0 ? _c : null,
        refreshToken: (_d = session === null || session === void 0 ? void 0 : session.refreshToken) !== null && _d !== void 0 ? _d : null,
        issuer: (_e = session === null || session === void 0 ? void 0 : session.issuer) !== null && _e !== void 0 ? _e : undefined,
        generation: (_f = session === null || session === void 0 ? void 0 : session.sessionGeneration) !== null && _f !== void 0 ? _f : null,
    };
}
async function commit(isServer, expected, refreshed) {
    var _a, _b;
    if (isServer) {
        const mgr = ServerSessionManager.instance;
        const session = await mgr.getSession();
        if (!session)
            return { outcome: 'superseded', token: null };
        const next = Object.assign(Object.assign(Object.assign({}, session), { idToken: refreshed.idToken, accessToken: refreshed.accessToken }), (refreshed.refreshToken ? { refreshToken: refreshed.refreshToken } : {}));
        mgr.setSession(next);
        return { outcome: 'applied', token: next.idToken };
    }
    const result = await getActiveSessionManager().commitRefreshedTokens(expected, refreshed.idToken, refreshed.accessToken, refreshed.refreshToken);
    // NEVER return refreshed.idToken directly. When the commit is refused or lost,
    // that token was not stored, and using it would authenticate the request as
    // whoever the refresh was for rather than whoever the session now belongs to.
    return { outcome: result.outcome, token: (_b = (_a = result.session) === null || _a === void 0 ? void 0 : _a.idToken) !== null && _b !== void 0 ? _b : null };
}
/**
 * Get a token that is safe to send right now, refreshing if needed.
 *
 * `null` means anonymous is fine. A throw means there is a session that cannot be
 * used; the caller must surface it rather than downgrade to anonymous.
 */
export async function getFreshAuthToken(isServer = false, options = {}) {
    const { idToken, refreshToken, issuer, generation } = await readSessionParts(isServer);
    // No session at all: anonymous is legitimate.
    if (!idToken)
        return null;
    const force = options.force === true;
    const freshness = classifyToken(idToken);
    if (!force && freshness === 'fresh')
        return idToken;
    // Still valid for a little longer, so a failed refresh can fall back to it —
    // but never when forced, since the server has already refused this exact token.
    const stillUsable = !force && freshness === 'expiring-soon';
    if (!refreshToken) {
        if (stillUsable)
            return idToken;
        // Nothing can revive this session. Drop it so isAuthenticated() stops lying
        // and the UI is told, then fail loudly instead of going anonymous.
        await clearDeadSession(isServer, generation);
        throw makeAuthExpiredError('Authentication expired and no refresh token is available');
    }
    const config = await getConfig();
    const key = transactionKey(isServer ? 'server' : 'web', config.appId, generation, refreshToken);
    const existing = refreshTransactions.get(key);
    if (existing)
        return await existing;
    const transaction = (async () => {
        var _a, _b, _c;
        try {
            const refreshed = await refreshSession(refreshToken, issuer);
            if (!(refreshed === null || refreshed === void 0 ? void 0 : refreshed.idToken) || !(refreshed === null || refreshed === void 0 ? void 0 : refreshed.accessToken)) {
                throw makeAuthExpiredError('Authentication refresh returned an incomplete session');
            }
            const { outcome, token } = await commit(isServer, { generation, idToken }, refreshed);
            if (outcome === 'superseded') {
                // A login or logout landed while we were on the network. Refuse rather
                // than authenticate this request as the principal that just left.
                throw makeAuthChangedError('The signed-in account changed while this request was in flight');
            }
            // applied / already-equal / same-generation-winner are all successes: the
            // stored session is valid for this same login either way.
            if (!token) {
                throw makeAuthExpiredError('Authentication refresh produced no usable token');
            }
            return token;
        }
        catch (error) {
            if (isAuthExpiredError(error))
                throw error;
            // Log the STATUS only: the axios error carries config.data, which holds
            // the refresh token, and must never reach a console or log sink.
            const status = (_c = (_b = (_a = error === null || error === void 0 ? void 0 : error.response) === null || _a === void 0 ? void 0 : _a.status) !== null && _b !== void 0 ? _b : error === null || error === void 0 ? void 0 : error.code) !== null && _c !== void 0 ? _c : 'unknown';
            if (isDefinitiveRejection(error)) {
                // The issuer says this refresh token is dead. Drop the session.
                await clearDeadSession(isServer, generation);
                throw makeAuthExpiredError('Authentication expired and refresh was rejected', status);
            }
            // TRANSIENT: network blip, 5xx, timeout. Keep the session — it may well
            // work in a moment. If the access token is still inside its skew window
            // it is valid right now, so use it rather than failing the request.
            if (stillUsable)
                return idToken;
            throw makeAuthExpiredError('Authentication expired and refresh failed', status);
        }
        finally {
            refreshTransactions.delete(key);
        }
    })();
    refreshTransactions.set(key, transaction);
    return await transaction;
}
async function clearDeadSession(isServer, generation) {
    if (isServer) {
        try {
            ServerSessionManager.instance.clearSession();
        }
        catch ( /* best effort */_a) { /* best effort */ }
        return;
    }
    try {
        // Conditional: never remove a session that a concurrent login already replaced.
        await getActiveSessionManager().clearSessionIfGeneration(generation);
    }
    catch ( /* a clear failure must not mask the auth error we are about to throw */_b) { /* a clear failure must not mask the auth error we are about to throw */ }
}
/** Test seam: drop any in-flight refresh transactions. */
export function resetRefreshTransactions() {
    refreshTransactions.clear();
}
